/**
 * Spellbook reads/writes: API when real account, IndexedDB when anonymous (M10).
 */

import { apiClient } from '@/api/client';
import * as spellbookLocalStore from '@/utils/spellbookLocalStore';
import { hasRealAccountSession } from '@/utils/authSession';
import {
  normalizeSpellSlots,
  getSlotAvailability,
  availabilityToUsed,
  autoSlotsForClassLevel,
} from '@/utils/spellbook';

function useServerSpellbooks() {
  return hasRealAccountSession();
}

function spellKey(spell) {
  return String(spell?.id ?? spell?.index ?? spell?.slug ?? spell?.name ?? '')
    .trim()
    .toLowerCase();
}

function normalizeApiBook(b) {
  if (!b) return b;
  return { ...b, spell_slots: normalizeSpellSlots(b.spell_slots) };
}

/** @returns {Promise<any[]>} */
export async function listSpellbooks() {
  if (useServerSpellbooks()) {
    const res = await apiClient.getSpellbooks();
    const books = Array.isArray(res?.spellbooks) ? res.spellbooks : [];
    return books.map(normalizeApiBook);
  }
  const local = await spellbookLocalStore.getAll();
  return local.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
}

/** @returns {Promise<{ spellbook: any }>} */
export async function createSpellbook({ name, linked_character_id }) {
  if (useServerSpellbooks()) {
    const raw = linked_character_id;
    const lid =
      raw == null || raw === ''
        ? null
        : Number.isFinite(Number(raw))
          ? Number(raw)
          : null;
    return apiClient.createSpellbook({ name, linked_character_id: lid });
  }
  const lid =
    linked_character_id != null && linked_character_id !== ''
      ? Number.isFinite(Number(linked_character_id))
        ? Number(linked_character_id)
        : String(linked_character_id)
      : null;
  const book = await spellbookLocalStore.put({
    id: spellbookLocalStore.generateLocalSpellbookId(),
    name,
    linked_character_id: lid,
    spells: [],
    spell_slots: normalizeSpellSlots({}),
    cast_history: [],
  });
  return { spellbook: book };
}

/** @returns {Promise<void>} */
export async function deleteSpellbook(spellbookId) {
  if (useServerSpellbooks()) {
    await apiClient.deleteSpellbook(spellbookId);
    return;
  }
  await spellbookLocalStore.remove(Number(spellbookId));
}

/** @returns {Promise<{ spellbook: any }>} */
export async function addSpellToSpellbook(spellbookId, spell) {
  if (useServerSpellbooks()) {
    return apiClient.addSpellToSpellbook(spellbookId, spell);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const key = spellKey(spell);
  const spells = [...(book.spells || [])];
  const idx = spells.findIndex((s) => spellKey(s) === key);
  const nextSpell = { ...spell };
  if (idx >= 0) spells[idx] = nextSpell;
  else spells.push(nextSpell);
  const updated = await spellbookLocalStore.put({ ...book, spells });
  return { spellbook: updated };
}

/** @returns {Promise<{ spellbook: any }>} */
export async function removeSpellFromSpellbook(spellbookId, spellId) {
  if (useServerSpellbooks()) {
    return apiClient.removeSpellFromSpellbook(spellbookId, spellId);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const sid = String(spellId);
  const spells = (book.spells || []).filter((s) => {
    const k = String(s?.id ?? s?.index ?? s?.slug ?? s?.name ?? '');
    return k !== sid && spellKey(s) !== sid.toLowerCase();
  });
  const updated = await spellbookLocalStore.put({ ...book, spells });
  return { spellbook: updated };
}

/** @returns {Promise<{ spellbook: any }>} */
export async function updateSpellbookSlot(spellbookId, data) {
  if (useServerSpellbooks()) {
    return apiClient.updateSpellbookSlot(spellbookId, data);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const { level, max, used, avail } = data;
  const key = `level_${level}`;
  const prev = book.spell_slots[key] || { max: 0, used: 0 };
  const nextMax = Math.max(0, Number(max) || 0);
  let nextAvail;
  if (nextMax === 0) {
    nextAvail = [];
  } else if (Array.isArray(avail) && avail.length === nextMax) {
    nextAvail = avail.map((x) => (x === 1 || x === true ? 1 : 0));
  } else {
    const u = Math.max(0, Math.min(nextMax, Number(used) || 0));
    nextAvail = Array.from({ length: nextMax }, (_, i) => (i < nextMax - u ? 1 : 0));
  }
  const nextUsed = nextMax > 0 ? availabilityToUsed(nextMax, nextAvail) : 0;
  const spell_slots = {
    ...book.spell_slots,
    [key]: nextMax > 0 ? { max: nextMax, used: nextUsed, avail: nextAvail } : { max: 0, used: 0 },
  };
  const updated = await spellbookLocalStore.put({ ...book, spell_slots: normalizeSpellSlots(spell_slots) });
  return { spellbook: updated };
}

/** @returns {Promise<{ spellbook: any }>} */
export async function updateSpellbook(spellbookId, payload) {
  if (useServerSpellbooks()) {
    return apiClient.updateSpellbook(spellbookId, payload);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const merged = {
    ...book,
    ...payload,
    id: book.id,
    spell_slots: normalizeSpellSlots(payload.spell_slots ?? book.spell_slots),
  };
  const updated = await spellbookLocalStore.put(merged);
  return { spellbook: updated };
}

/** @returns {Promise<{ spellbook: any }>} */
export async function resetSpellbookSlots(spellbookId) {
  if (useServerSpellbooks()) {
    return apiClient.resetSpellbookSlots(spellbookId);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const spell_slots = { ...book.spell_slots };
  for (let L = 1; L <= 9; L++) {
    const key = `level_${L}`;
    const prev = spell_slots[key];
    if (!prev || !prev.max) continue;
    const max = prev.max;
    const avail = Array.from({ length: max }, () => 1);
    spell_slots[key] = { max, used: 0, avail };
  }
  const updated = await spellbookLocalStore.put({
    ...book,
    spell_slots: normalizeSpellSlots(spell_slots),
  });
  return { spellbook: updated };
}

/** @returns {Promise<{ spellbook: any }>} */
export async function castSpellFromSpellbook(spellbookId, data) {
  if (useServerSpellbooks()) {
    return apiClient.castSpellFromSpellbook(spellbookId, data);
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const spellLevel = Number(data.spell_level ?? 0);
  const castLevel = Number(data.cast_level ?? 0);
  const spell_slots = { ...book.spell_slots };
  if (spellLevel > 0 && castLevel > 0) {
    const key = `level_${castLevel}`;
    const entry = spell_slots[key] || { max: 0, used: 0 };
    const max = entry.max || 0;
    if (max > 0) {
      const avail = [...getSlotAvailability(entry)];
      for (let i = avail.length - 1; i >= 0; i--) {
        if (avail[i] === 1) {
          avail[i] = 0;
          break;
        }
      }
      const used = availabilityToUsed(max, avail);
      spell_slots[key] = { max, used, avail };
    }
  }
  const cast_history = [
    ...(book.cast_history || []),
    {
      spell_name: data.spell_name,
      spell_level: spellLevel,
      cast_level: castLevel,
      created_at: new Date().toISOString(),
    },
  ];
  const updated = await spellbookLocalStore.put({
    ...book,
    spell_slots: normalizeSpellSlots(spell_slots),
    cast_history,
  });
  return { spellbook: updated };
}

/**
 * Apply auto slots from a linked character (local demo).
 * @param {number} spellbookId
 * @param {{ class?: string, level?: number }} character
 */
export async function applyAutoSlotsFromCharacter(spellbookId, character) {
  if (useServerSpellbooks()) {
    const slots = autoSlotsForClassLevel(character?.class, Number(character?.level || 1));
    return apiClient.updateSpellbook(spellbookId, { spell_slots: slots });
  }
  const book = await spellbookLocalStore.getById(Number(spellbookId));
  if (!book) throw new Error('Spell book not found');
  const slots = autoSlotsForClassLevel(character?.class, Number(character?.level || 1));
  const updated = await spellbookLocalStore.put({
    ...book,
    spell_slots: normalizeSpellSlots(slots),
  });
  return { spellbook: updated };
}

/**
 * Migrate demo spellbooks (negative local ids) to the server after login.
 * @param {Map<string, number>} [characterIdMap] local character id → server id
 * @returns {Promise<{ synced: number }>}
 */
export async function syncLocalSpellbooksToServer(characterIdMap = new Map()) {
  if (!useServerSpellbooks()) return { synced: 0 };
  let locals = [];
  try {
    locals = await spellbookLocalStore.getAll();
  } catch {
    return { synced: 0 };
  }
  const demoBooks = (locals || []).filter((b) => Number(b.id) < 0);
  if (demoBooks.length === 0) return { synced: 0 };

  let serverList = [];
  try {
    const res = await apiClient.getSpellbooks();
    serverList = Array.isArray(res?.spellbooks) ? res.spellbooks : [];
  } catch {
    return { synced: 0 };
  }

  let synced = 0;
  for (const book of demoBooks) {
    const nameKey = String(book.name || '').trim().toLowerCase();
    const dup = nameKey
      ? serverList.find((s) => String(s.name || '').trim().toLowerCase() === nameKey)
      : null;
    if (dup?.id) {
      try {
        await spellbookLocalStore.remove(book.id);
      } catch {
        // ignore
      }
      continue;
    }
    let linked_character_id = book.linked_character_id;
    if (linked_character_id != null && linked_character_id !== '') {
      const key = String(linked_character_id);
      if (characterIdMap instanceof Map && characterIdMap.has(key)) {
        linked_character_id = characterIdMap.get(key);
      } else if (!Number.isFinite(Number(linked_character_id))) {
        linked_character_id = null;
      }
    } else {
      linked_character_id = null;
    }
    try {
      const res = await apiClient.createSpellbook({
        name: book.name || 'Spellbook',
        linked_character_id:
          linked_character_id != null && Number.isFinite(Number(linked_character_id))
            ? Number(linked_character_id)
            : null,
      });
      const created = res?.spellbook;
      if (!created?.id) continue;
      const newId = created.id;
      for (const sp of book.spells || []) {
        await apiClient.addSpellToSpellbook(newId, sp).catch(() => {});
      }
      if (book.spell_slots && Object.keys(book.spell_slots).length > 0) {
        await apiClient
          .updateSpellbook(newId, { spell_slots: normalizeSpellSlots(book.spell_slots) })
          .catch(() => {});
      }
      await spellbookLocalStore.remove(book.id).catch(() => {});
      serverList.push(created);
      synced++;
    } catch {
      // skip
    }
  }
  return { synced };
}
